import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { playerProfileService } from '../../services/playerMasterService';
import { adminUserManagementService } from '../../services/adminUserManagementService';

import PlayerManagementTable from './components/PlayerManagementTable';
import PlayerEditModal from './components/PlayerEditModal';
import TransfermarktImportModal from './components/TransfermarktImportModal';
import AdminContentManager from './components/AdminContentManager';
import AdminVisitorManager from './components/AdminVisitorManager';
import AdminPlayerStatsManager from './components/AdminPlayerStatsManager';
import AdminVisitorAnalyticsManager from './components/AdminVisitorAnalyticsManager';
import AdminMessageManager from './components/AdminMessageManager';
import AdminAnalyticsDashboard from './components/AdminAnalyticsDashboard';
import RealtimeMonitoringDashboard from './components/RealtimeMonitoringDashboard';

import Select from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';

const AdminDashboard = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isContentManagerOpen, setIsContentManagerOpen] = useState(false);
  const [isVisitorManagerOpen, setIsVisitorManagerOpen] = useState(false);
  const [isMessageManagerOpen, setIsMessageManagerOpen] = useState(false);
  const [isUserConfirmModalOpen, setIsUserConfirmModalOpen] = useState(false);
  const [isStatsManagerOpen, setIsStatsManagerOpen] = useState(false);
  const [isAnalyticsDashboardOpen, setIsAnalyticsDashboardOpen] = useState(false);
  const [isMonitoringDashboardOpen, setIsMonitoringDashboardOpen] = useState(false);
  const [isVisitorAnalyticsOpen, setIsVisitorAnalyticsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPosition, setFilterPosition] = useState('all');
  const [unconfirmedUsers, setUnconfirmedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmationData, setConfirmationData] = useState({
    profileId: '',
    notes: ''
  });
  const [confirmedUsers, setConfirmedUsers] = useState([]);
  const [isConfirmedUsersModalOpen, setIsConfirmedUsersModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, playerId: null, playerName: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchPlayers();
    if (isAdmin) {
      fetchUnconfirmedUsers();
      fetchConfirmedUsers();
    }
  }, [isAdmin]);

  const fetchPlayers = async () => {
    setLoading(true);
    try {
      // Use getAllForAdmin to see all players including unverified and admin-created
      const response = await playerProfileService?.getAllForAdmin();
      setPlayers(response?.data || []);
    } catch (error) {
      console.error('Error fetching players:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnconfirmedUsers = async () => {
    try {
      const response = await adminUserManagementService?.getUnconfirmedUsers();
      setUnconfirmedUsers(response?.data || []);
    } catch (error) {
      console.error('Error fetching unconfirmed users:', error);
    }
  };

  const fetchConfirmedUsers = async () => {
    try {
      const response = await adminUserManagementService?.getConfirmedUsers();
      setConfirmedUsers(response?.data || []);
    } catch (error) {
      console.error('Error fetching confirmed users:', error);
    }
  };

  const handleCreateNew = () => {
    if (!isAdmin) return;
    setSelectedPlayer(null);
    setIsEditModalOpen(true);
  };

  const handleImportFromTransfermarkt = () => {
    if (!isAdmin) return;
    setIsImportModalOpen(true);
  };

  const handleImportSuccess = () => {
    fetchPlayers();
  };

  const handleEdit = (player) => {
    if (!isAdmin) return;
    setSelectedPlayer(player);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (playerId) => {
    if (!isAdmin) return;
    const player = players?.find(p => p?.id === playerId);
    setDeleteConfirm({ open: true, playerId, playerName: player?.fullName || 'this player' });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm?.playerId) return;
    setIsDeleting(true);
    try {
      const result = await playerProfileService?.delete(deleteConfirm?.playerId);
      if (result?.error) {
        alert('Failed to delete player: ' + (result?.error?.message || 'Unknown error'));
        return;
      }
      setDeleteConfirm({ open: false, playerId: null, playerName: '' });
      fetchPlayers();
    } catch (error) {
      console.error('Error deleting player:', error);
      alert('Failed to delete player: ' + error?.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSave = async () => {
    setIsEditModalOpen(false);
    fetchPlayers();
  };

  const handleSavePlayer = async (savedPlayer) => {
    // Refresh the player list to show the newly created/updated player
    await fetchPlayers();
  };

  const handleConfirmUser = (user) => {
    if (!isAdmin) return;
    setSelectedUser(user);
    setConfirmationData({ profileId: '', notes: '' });
    setIsUserConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!selectedUser || !confirmationData?.profileId) {
      alert('Please select a player profile to assign');
      return;
    }

    try {
      await adminUserManagementService?.confirmUser(
        selectedUser?.id,
        confirmationData?.profileId,
        confirmationData?.notes
      );
      await fetchUnconfirmedUsers();
      await fetchConfirmedUsers();
      setIsUserConfirmModalOpen(false);
      setSelectedUser(null);
      setConfirmationData({ profileId: '', notes: '' });
      alert('User confirmed and profile assigned successfully!');
    } catch (error) {
      console.error('Error confirming user:', error);
      alert('Failed to confirm user: ' + error?.message);
    }
  };

  const handleUnassignProfile = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to unassign the player profile from ${userName}? They will still be confirmed but won't have access to profile analytics.`)) return;
    
    try {
      await adminUserManagementService?.unassignProfile(userId);
      await fetchConfirmedUsers();
      alert('Player profile unassigned successfully!');
    } catch (error) {
      console.error('Error unassigning profile:', error);
      alert('Failed to unassign profile: ' + error?.message);
    }
  };

  const filteredPlayers = players?.filter(player => {
    const matchesSearch = player?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         player?.currentClub?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesPosition = filterPosition === 'all' || player?.primaryPosition === filterPosition;
    return matchesSearch && matchesPosition;
  });

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - ProX11Market</title>
      </Helmet>
      <Header />
      <div className="min-h-screen pt-16" style={{ background: 'linear-gradient(135deg, #060810 0%, #0d1117 50%, #081008 100%)' }}>
        {/* Premium Header Banner */}
        <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1428 0%, #0d1b3e 50%, #081a10 100%)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          {/* Football field lines */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] rounded-full" style={{ border: '1px solid rgba(255,255,255,0.03)' }} />
            <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 45px, rgba(255,255,255,0.012) 45px, rgba(255,255,255,0.012) 46px)' }} />
          </div>
          <div className="absolute top-0 left-0 w-[500px] h-full rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(0,100,255,0.07)' }} />
          <div className="absolute bottom-0 right-0 w-[500px] h-full rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(0,180,80,0.05)' }} />

          <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  {/* EA Sports mini badge */}
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(255,107,0,0.12)', border: '1px solid rgba(255,107,0,0.38)' }}>
                    <svg viewBox="0 0 90 30" width="72" height="24">
                      <text x="0" y="12" fontFamily="Arial Black, sans-serif" fontSize="8" fontWeight="900" fill="#FF6B00" letterSpacing="1">EA SPORTS</text>
                      <text x="0" y="28" fontFamily="Arial Black, sans-serif" fontSize="18" fontWeight="900" fill="white">FC</text>
                      <rect x="24" y="17" width="66" height="2" fill="#FF6B00"/>
                      <text x="26" y="27" fontFamily="Arial, sans-serif" fontSize="8" fontWeight="700" fill="#FF6B00" letterSpacing="2">24/25</text>
                    </svg>
                  </div>
                  {/* PS5 mini badge */}
                  <div className="flex items-center px-3 py-1.5 rounded-lg" style={{ background: 'rgba(0,52,143,0.2)', border: '1px solid rgba(0,100,255,0.38)' }}>
                    <svg viewBox="0 0 55 24" width="44" height="19">
                      <text x="0" y="20" fontFamily="Arial Black, sans-serif" fontSize="20" fontWeight="900" fill="white" letterSpacing="-0.5">PS5</text>
                    </svg>
                  </div>
                  <div className="w-px h-6" style={{ background: 'rgba(255,255,255,0.12)' }} />
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)' }}>Admin Control Center</span>
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight">
                  Admin Dashboard
                </h1>
                <p className="text-sm md:text-base mt-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Manage player profiles, content, and platform data
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => window.location.href = '/admin-player-verification'}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #0078ff, #0050cc)', boxShadow: '0 4px 15px rgba(0,120,255,0.25)' }}
                >
                  <Icon name="ShieldCheck" size={16} color="white" />
                  Player Verification
                </button>
                <button
                  onClick={() => window.location.href = '/admin-audit-dashboard'}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="FileText" size={16} />
                  Audit Dashboard
                </button>
                <button
                  onClick={() => setIsAnalyticsDashboardOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="BarChart3" size={16} />
                  Analytics
                </button>
                <button
                  onClick={() => setIsMonitoringDashboardOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #FF6B00, #FF9500)', boxShadow: '0 4px 15px rgba(255,107,0,0.25)' }}
                >
                  <Icon name="Activity" size={16} color="white" />
                  Real-Time Monitoring
                </button>
                <button
                  onClick={() => setIsStatsManagerOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="BarChart" size={16} />
                  Player Metrics
                </button>
                <button
                  onClick={() => setIsVisitorManagerOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="Eye" size={16} />
                  Visitor Tracking
                </button>
                <button
                  onClick={() => setIsVisitorAnalyticsOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="Users" size={16} />
                  Profile Visitors
                </button>
                <button
                  onClick={() => setIsMessageManagerOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="MessageSquare" size={16} />
                  Messages
                </button>
                <button
                  onClick={() => setIsContentManagerOpen(true)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="MessageSquare" size={16} />
                  Content Manager
                </button>
                <button
                  onClick={handleImportFromTransfermarkt}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)' }}
                >
                  <Icon name="Upload" size={16} />
                  Import
                </button>
                <button
                  onClick={handleCreateNew}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #00a651, #007a3d)', boxShadow: '0 4px 15px rgba(0,166,81,0.25)' }}
                >
                  <Icon name="Plus" size={16} color="white" />
                  New Player
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* User Confirmation Alert */}
          {isAdmin && unconfirmedUsers?.length > 0 && (
            <div className="rounded-2xl p-4 mb-6" style={{ background: 'rgba(255,184,0,0.08)', border: '1px solid rgba(255,184,0,0.28)' }}>
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={24} color="#FFB800" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-white mb-2">
                    {unconfirmedUsers?.length} User{unconfirmedUsers?.length !== 1 ? 's' : ''} Awaiting Confirmation
                  </h3>
                  <div className="space-y-2">
                    {unconfirmedUsers?.map(user => (
                      <div key={user?.id} className="flex items-center justify-between rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.08)' }}>
                            <Icon name="User" size={20} color="rgba(255,255,255,0.5)" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{user?.fullName}</p>
                            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>{user?.email} &bull; {user?.role}</p>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => handleConfirmUser(user)}>Confirm &amp; Assign Profile</Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats Cards - Premium Dark */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Players', value: players?.length || 0, icon: 'Users', color: '#0078ff', glow: 'rgba(0,120,255,0.15)' },
              { label: 'Verified Profiles', value: players?.filter(p => p?.verifiedStatus)?.length || 0, icon: 'ShieldCheck', color: '#00d68f', glow: 'rgba(0,214,143,0.15)' },
              { label: 'Available for Transfer', value: players?.filter(p => p?.availableForTransfer)?.length || 0, icon: 'TrendingUp', color: '#FF6B00', glow: 'rgba(255,107,0,0.15)' },
              { label: 'FIFA Verified', value: players?.filter(p => p?.fifaVerified)?.length || 0, icon: 'Award', color: '#FFB800', glow: 'rgba(255,184,0,0.15)' }
            ]?.map((card, i) => (
              <div
                key={i}
                className="rounded-2xl p-5 transition-all hover:scale-[1.02]"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', boxShadow: `0 4px 20px ${card?.glow}` }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{card?.label}</p>
                    <p className="text-3xl font-black text-white">{card?.value}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: `${card?.color}18`, border: `1px solid ${card?.color}35` }}>
                    <Icon name={card?.icon} size={24} color={card?.color} />
                  </div>
                </div>
                <div className="mt-3 h-0.5 rounded-full" style={{ background: `linear-gradient(90deg, ${card?.color}, transparent)` }} />
              </div>
            ))}
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Icon 
                name="Search" 
                size={20} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
              />
              <input
                type="text"
                placeholder="Search by player name or club..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <select
              value={filterPosition}
              onChange={(e) => setFilterPosition(e?.target?.value)}
              className="px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="all">All Positions</option>
              <option value="goalkeeper">Goalkeeper</option>
              <option value="defender">Defender</option>
              <option value="midfielder">Midfielder</option>
              <option value="forward">Forward</option>
            </select>
          </div>
        </div>

        {/* Players Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <PlayerManagementTable
            players={filteredPlayers}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
          />
        )}
      </div>
      {/* Edit Modal */}
      {isEditModalOpen && (
        <PlayerEditModal
          player={selectedPlayer}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSave}
        />
      )}
      {/* Import Modal */}
      {isImportModalOpen && (
        <TransfermarktImportModal
          onClose={() => setIsImportModalOpen(false)}
          onImportSuccess={handleImportSuccess}
        />
      )}
      {/* Content Manager Modal */}
      {isContentManagerOpen && (
        <AdminContentManager
          onClose={() => setIsContentManagerOpen(false)}
        />
      )}
      {isVisitorManagerOpen && (
        <AdminVisitorManager onClose={() => setIsVisitorManagerOpen(false)} />
      )}
      {isStatsManagerOpen && (
        <AdminPlayerStatsManager onClose={() => setIsStatsManagerOpen(false)} />
      )}
      {isVisitorAnalyticsOpen && (
        <AdminVisitorAnalyticsManager onClose={() => setIsVisitorAnalyticsOpen(false)} />
      )}
      {isMessageManagerOpen && (
        <AdminMessageManager onClose={() => setIsMessageManagerOpen(false)} />
      )}
      {isAnalyticsDashboardOpen && (
        <AdminAnalyticsDashboard
          onClose={() => setIsAnalyticsDashboardOpen(false)}
        />
      )}
      {isMonitoringDashboardOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg w-full max-w-7xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">Real-Time Monitoring Dashboard</h2>
              <button
                onClick={() => setIsMonitoringDashboardOpen(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="p-6">
              <RealtimeMonitoringDashboard />
            </div>
          </div>
        </div>
      )}
      {/* User Confirmation Modal */}
      {isUserConfirmModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-foreground">
                  Confirm User & Assign Profile
                </h2>
                <button
                  onClick={() => setIsUserConfirmModalOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-semibold text-foreground mb-3">User Information</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Icon name="User" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{selectedUser?.fullName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Mail" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{selectedUser?.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Shield" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground capitalize">{selectedUser?.role}</span>
                  </div>
                </div>
              </div>

              {/* Profile Assignment */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Assign Player Profile *
                </label>
                <select
                  value={confirmationData?.profileId}
                  onChange={(e) => setConfirmationData({ ...confirmationData, profileId: e?.target?.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select a player profile...</option>
                  {players?.map(player => (
                    <option key={player?.id} value={player?.id}>
                      {player?.fullName} - {player?.primaryPosition} ({player?.currentClub || 'Free Agent'})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  This will link the user account to the selected player profile, giving them access to profile analytics and visitor data.
                </p>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Confirmation Notes (Optional)
                </label>
                <textarea
                  value={confirmationData?.notes}
                  onChange={(e) => setConfirmationData({ ...confirmationData, notes: e?.target?.value })}
                  placeholder="Add any notes about this confirmation..."
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>

              {/* Info Box */}
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-foreground">
                    <p className="font-semibold mb-1">What happens after confirmation?</p>
                    <ul className="space-y-1 text-muted-foreground">
                      <li>• User will get full access to their dashboard</li>
                      <li>• They can view profile analytics, visitor stats, and club interests</li>
                      <li>• The "Awaiting Confirmation" message will be removed</li>
                      <li>• They will be linked to the selected player profile</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-border flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsUserConfirmModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmSubmit}
                disabled={!confirmationData?.profileId}
              >
                Confirm & Assign
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Confirmed Users Management Modal */}
      {isConfirmedUsersModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-foreground">
                    Confirmed Users Management
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Manage player profile assignments for confirmed users
                  </p>
                </div>
                <button
                  onClick={() => setIsConfirmedUsersModalOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="X" size={24} />
                </button>
              </div>
            </div>

            <div className="p-6">
              {confirmedUsers?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Users" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No confirmed users yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {confirmedUsers?.map(user => (
                    <div key={user?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon name="User" size={24} className="text-primary" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-foreground">{user?.fullName}</h3>
                              <span className="px-2 py-0.5 bg-success/10 text-success text-xs rounded-full">
                                Confirmed
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{user?.email}</p>
                            <div className="flex items-center gap-4 text-sm">
                              <div className="flex items-center gap-1">
                                <Icon name="Shield" size={14} className="text-muted-foreground" />
                                <span className="text-muted-foreground capitalize">{user?.role}</span>
                              </div>
                              {user?.assignedProfile && (
                                <div className="flex items-center gap-1">
                                  <Icon name="Link" size={14} className="text-primary" />
                                  <span className="text-foreground">
                                    {user?.assignedProfile?.full_name} ({user?.assignedProfile?.primary_position})
                                  </span>
                                </div>
                              )}
                              {!user?.assignedProfile && (
                                <div className="flex items-center gap-1">
                                  <Icon name="AlertCircle" size={14} className="text-warning" />
                                  <span className="text-warning">No profile assigned</span>
                                </div>
                              )}
                            </div>
                            {user?.confirmationNotes && (
                              <div className="mt-2 p-2 bg-muted/50 rounded text-xs text-muted-foreground">
                                <strong>Notes:</strong> {user?.confirmationNotes}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {user?.assignedProfile && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnassignProfile(user?.id, user?.fullName)}
                              iconName="Unlink"
                            >
                              Unassign
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border flex items-center justify-end">
              <Button
                onClick={() => setIsConfirmedUsersModalOpen(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Player Confirmation Modal */}
      {deleteConfirm?.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-2xl w-full max-w-md border border-border">
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Icon name="Trash2" size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary">Delete Player</h3>
                  <p className="text-sm text-text-secondary">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-text-primary mb-2">
                Are you sure you want to permanently delete <span className="font-semibold">{deleteConfirm?.playerName}</span>?
              </p>
              <p className="text-sm text-text-secondary">
                All associated data will be removed including performance stats, career history, transfer history, messages, media, and analytics.
              </p>
            </div>
            <div className="px-6 pb-6 flex items-center justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ open: false, playerId: null, playerName: '' })}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="bg-red-600 hover:bg-red-700 text-white border-red-600"
              >
                {isDeleting ? 'Deleting...' : 'Delete Player'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminDashboard;